import { AlertTriangle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { formatCurrency } from 'app/utils/formatters';
import { DataSource } from 'app/types/visualizations';
import { Documents } from 'app/types/visualizations';
import { Card, CardContent, CardHeader, CardTitle } from '@shadcn/ui';
import EmissionsTrendAnalysis from 'app/pages/project/tabs/analysis/EmissionsTrendAnalysis';
import EnergyUsageBreakdown from 'app/pages/project/tabs/analysis/EnergyUsageBreakdown';
import EmissionsByService from 'app/pages/project/tabs/analysis/EmissionsByService';
import { BillingPeriod, MonthlyEmissions } from 'app/components/service/types';
import { ProjectEmissionsRanking } from './ProjectEmissionsRanking';
import { ProjectPenaltiesRanking } from './ProjectPenaltiesRanking';
import { ProjectInsight } from 'app/services/project-services';

interface EnergyOperationsProps {
  year: number;
  dataSource: DataSource;
  documents: Documents;
  meterReadings: Documents;
  isPortfolio: boolean;
  portfolioProjectsInsights?: ProjectInsight[];
}

export const EnergyOperations = ({
  year,
  dataSource,
  documents,
  meterReadings,
  isPortfolio,
  portfolioProjectsInsights,
}: EnergyOperationsProps) => {
  const { name } = useParams();
  const data = dataSource === 'utility-bills' ? documents : meterReadings;

  const yearlyData = data?.yearlyData || {};
  let annualThresholdCO2: number | null = null;
  let actualEmissions: number | null = null;
  let estimatedPenalty: number | null = null;
  if (yearlyData) {
    const sortedYears = Object.keys(yearlyData).sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    const yearWithFullData = sortedYears.find((year) => {
      const yearData = yearlyData[year];
      return Object.entries(yearData.monthsCoveredByService || {}).some(
        ([service, months]) => service !== 'water' && months === 12
      );
    });

    if (yearWithFullData) {
      const yearData = yearlyData[year];
      annualThresholdCO2 = yearData?.threshold;
      actualEmissions = yearData?.actualEmissions;
      estimatedPenalty = yearData?.estimatedAnnualPenalty;
    }
  }

  const yearsData = Object.values(yearlyData);

  const processMonthlyEmissions = (data?: Documents): MonthlyEmissions[] => {
    if (!data) return [];

    const year2025Data: MonthlyEmissions[] = [];
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyEmissions = new Array(12).fill(0);

    const electricBillingPeriods = (data?.electric?.billingPeriods || []) as unknown as BillingPeriod[];
    const gasBillingPeriods = (data?.gas?.billingPeriods || []) as unknown as BillingPeriod[];
    const steamBillingPeriods = (data?.steam?.billingPeriods || []) as unknown as BillingPeriod[];
    const oilBillingPeriods = (data?.oil?.billingPeriods || []) as unknown as BillingPeriod[];

    // Process electric data
    electricBillingPeriods.forEach((period) => {
      const startDate = new Date(period.billing_start_date);
      if (startDate.getFullYear() === year) {
        const month = startDate.getMonth();
        monthlyEmissions[month] += period.emissions;
      }
    });

    // Process gas data
    gasBillingPeriods.forEach((period) => {
      const startDate = new Date(period.billing_start_date);
      if (startDate.getFullYear() === year) {
        const month = startDate.getMonth();
        monthlyEmissions[month] += period.emissions;
      }
    });

    // Process steam data
    steamBillingPeriods.forEach((period) => {
      const startDate = new Date(period.billing_start_date);
      if (startDate.getFullYear() === year) {
        const month = startDate.getMonth();
        monthlyEmissions[month] += period.emissions;
      }
    });

    // Process oil data
    oilBillingPeriods.forEach((period) => {
      const startDate = new Date(period.billing_start_date);
      if (startDate.getFullYear() === year) {
        const month = startDate.getMonth();
        monthlyEmissions[month] += period.emissions;
      }
    });

    // Create final data array
    monthlyEmissions.forEach((emissions, index) => {
      year2025Data.push({
        month: monthNames[index],
        emissions: Number(emissions.toFixed(2)),
      });
    });

    return year2025Data;
  };

  const monthlyEmissionsData = processMonthlyEmissions(data);

  return (
    <div className="space-y-4">
      {/* Emissions Status Card */}
      <Card className="w-full border border-gray-200 bg-white shadow-sm">
        <CardHeader className="border-b border-gray-100 pb-4">
          <CardTitle className="text-center text-lg font-medium text-gray-700">
            {isPortfolio ? `${name} Portfolio ` : ''}
            {year === new Date().getFullYear()
              ? `Emissions as of ${new Date().toLocaleString('default', { month: 'long' })}, ${year}`
              : `Emissions ${year}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="space-y-8">
            {/* Metrics Row */}
            <div className="grid grid-cols-3 gap-4">
              {/* Emissions Threshold */}
              <div className="flex flex-col items-center justify-center rounded-lg p-6">
                <h3 className="mb-2 text-sm font-medium text-gray-500">Emissions Threshold</h3>
                <div className="flex items-baseline gap-1">
                  <p className="text-2xl font-semibold text-gray-900">
                    {annualThresholdCO2?.toLocaleString() || 'N/A'}
                  </p>
                  <span className="text-sm text-gray-500">tCO₂e</span>
                </div>
              </div>
              {/* Actual Emissions */}
              <div className="flex flex-col items-center justify-center rounded-lg p-6">
                <h3 className="mb-2 text-sm font-medium text-gray-500">Current Emissions</h3>
                <div className="flex flex-col items-center gap-2">
                  <div className="flex items-baseline gap-1">
                    <p className="text-2xl font-semibold text-gray-900">{actualEmissions?.toLocaleString() || 'N/A'}</p>
                    <span className="text-sm text-gray-500">tCO₂e</span>
                    {annualThresholdCO2 && actualEmissions && actualEmissions > annualThresholdCO2 && (
                      <AlertTriangle className="ml-1 h-5 w-5 text-amber-500" />
                    )}
                  </div>
                  {annualThresholdCO2 && actualEmissions && (
                    <div className="flex w-full max-w-[200px] flex-col items-center gap-1.5">
                      <div className="h-1.5 w-full rounded-full bg-gray-100">
                        <div
                          className={`h-full rounded-full transition-all ${
                            actualEmissions > annualThresholdCO2 ? 'bg-amber-500' : 'bg-blue-500'
                          }`}
                          style={{
                            width: `${Math.min((actualEmissions / annualThresholdCO2) * 100, 100)}%`,
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        {((actualEmissions / annualThresholdCO2) * 100).toFixed(1)}% of threshold
                      </p>
                    </div>
                  )}
                </div>
              </div>
              {/* Emissions Penalty */}
              <div className="flex flex-col items-center justify-center rounded-lg p-6">
                <h3 className="mb-2 text-sm font-medium text-gray-500">Estimated Penalty</h3>
                <div className="flex items-baseline gap-1">
                  <p className="text-2xl font-semibold text-gray-900">
                    {estimatedPenalty ? formatCurrency(estimatedPenalty) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Portfolio Projects Ranked by Emissions */}
            {isPortfolio && portfolioProjectsInsights && (
              <div className="grid grid-cols-2 gap-4">
                <ProjectEmissionsRanking projectsWithInsights={portfolioProjectsInsights} year={year} />
                <ProjectPenaltiesRanking projectsWithInsights={portfolioProjectsInsights} year={year} />
              </div>
            )}

            {/* Charts Row */}
            <div className="grid grid-cols-2 gap-8">
              {/* Month over Month Emissions */}
              <div className="h-[500px]">
                <EmissionsTrendAnalysis monthlyEmissionsData={monthlyEmissionsData} />
              </div>

              {/* Emissions By Service */}
              <div className="h-[500px]">
                <EmissionsByService
                  sortedYears={Object.values(yearlyData).sort(
                    (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
                  )}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Energy Usage Card */}
      <Card className="w-full border border-gray-200 bg-white shadow-sm">
        <CardHeader className="border-b border-gray-100 pb-4">
          <CardTitle className="text-center text-lg font-medium text-gray-700">
            {isPortfolio ? `${name} Portfolio ` : ''}
            {year === new Date().getFullYear()
              ? `Energy Usage as of ${new Date().toLocaleString('default', { month: 'long' })}, ${year}`
              : `Energy Usage ${year}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-8">
            {/* Energy Usage Breakdown */}
            <div className="rounded-lg bg-background/5 p-4">
              <EnergyUsageBreakdown
                yearsData={yearsData}
                isPortfolio={isPortfolio}
                portfolioProjectsInsights={portfolioProjectsInsights}
                year={year}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
